import { FC, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { withHoverIcon } from '../../../hoc/withHoverIcon';
import { declOfNum } from '../../../utils/decOfNum';
import Button from '../../common/button';
import { CloseCircleCrossBtn } from '../../icons/close-circle-cross-btn';
import { Love } from '../../icons/reactions/love';
import { Joy } from '../../icons/reactions/joy';
import { Sadness } from '../../icons/reactions/sadness';
import { Fear } from '../../icons/reactions/fear';
import { Anger } from '../../icons/reactions/anger';
import type { IconProps } from '../../../types/icon.types';

const CloseCircleCrossBtnHover = withHoverIcon(CloseCircleCrossBtn);

const EMOTION_ORDER = ['Любовь', 'Радость', 'Грусть', 'Страх', 'Гнев'] as const;

const emotionIcons: Record<string, FC<IconProps>> = {
  Любовь: Love,
  Радость: Joy,
  Грусть: Sadness,
  Страх: Fear,
  Гнев: Anger,
};

const CLOSE_BTN_COLOR_PROPS = {
  defaultFill: '#828282',
  defaultFillBg: 'rgba(255, 255, 255, 0.8)',
  hoverFill: '#63656A',
  hoverFillBg: '#DEC05E',
};

export interface EmotionsGroupPsychologistResultsProps {
  expectedParticipantIds: string[];
  participantDisplayNames?: Record<string, string>;
  onClose: () => void;
  /** Панель развёрнута на всю высоту поверх видео */
  isExpanded?: boolean;
  /** Переключить развёрнутое состояние */
  onExpandToggle?: () => void;
  /** Вызывается при переключении таба: номер участника и его эмоции — для трансляции клиентам */
  onActiveTabChange?: (participantLabel: string, emo: string[]) => void;
}

const GROUP_EMOTIONS_INSTRUCTIONS = [
  { title: 'Как это работает:', lines: ['Клиенты видят список категорий эмоций и выбирают подходящие.'] },
  { title: 'Завершение работы:', lines: ['Нажмите Х (крестик).'] },
];

/**
 * Нижняя панель психолога в групповой терапии после «Запросить»:
 * Счётчик ответивших, «Клиенты выбирают…», затем табы с результатами по участникам.
 */
export const EmotionsGroupPsychologistResults: FC<EmotionsGroupPsychologistResultsProps> = ({
  expectedParticipantIds,
  participantDisplayNames,
  onClose,
  isExpanded = false,
  onExpandToggle,
  onActiveTabChange,
}) => {
  const clientSelectionsByParticipant = useAppSelector(
    (state) => state.emotionsGroupSlice.clientSelectionsByParticipant,
  );
  const emotionSettings = useAppSelector(
    (state) => state.psyToolsSlice.tools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings,
  );

  const respondedIds = expectedParticipantIds.filter((id) => clientSelectionsByParticipant[id]?.emo?.length > 0);
  const allResponded = expectedParticipantIds.length > 0 && respondedIds.length >= expectedParticipantIds.length;
  const [forceShowResults, setForceShowResults] = useState(false);
  const canShowResults = allResponded || forceShowResults;

  const [activeTabId, setActiveTabId] = useState<string | null>(null);

  const effectiveActiveId =
    activeTabId && respondedIds.includes(activeTabId)
      ? activeTabId
      : (respondedIds[0] ?? null);

  const participantNumber = (index: number) => index + 1;

  const initialTabSyncDone = useRef(false);
  useEffect(() => {
    if (!canShowResults || respondedIds.length === 0 || !onActiveTabChange) return;
    if (initialTabSyncDone.current) return;
    initialTabSyncDone.current = true;
    const firstId = respondedIds[0];
    setActiveTabId(firstId);
    const idx = expectedParticipantIds.indexOf(firstId);
    const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
    const labelForClient = participantDisplayNames?.[firstId] ?? numLabel;
    const emo = clientSelectionsByParticipant[firstId]?.emo ?? [];
    onActiveTabChange(labelForClient, emo);
  }, [canShowResults, respondedIds, expectedParticipantIds, clientSelectionsByParticipant, participantDisplayNames, onActiveTabChange]);

  const activeSelection = effectiveActiveId ? clientSelectionsByParticipant[effectiveActiveId] : null;
  const activeEmo = activeSelection?.emo ?? [];

  /** Группы эмоций по категориям (как в терапевтическом журнале): слева смайлик + название, справа теги */
  const emotionsByGroup = useMemo(() => {
    if (!activeSelection?.category?.length || !activeSelection.emo?.length || !emotionSettings?.length) {
      return [];
    }
    const emoSet = new Set(activeSelection.emo);
    const result: { category: string; items: string[] }[] = [];
    for (const cat of EMOTION_ORDER) {
      if (!activeSelection.category.includes(cat)) continue;
      const items = (emotionSettings.find((s) => s.name === cat)?.value ?? []).filter((e) => emoSet.has(e));
      if (items.length > 0) result.push({ category: cat, items });
    }
    return result;
  }, [activeSelection?.category, activeSelection?.emo, emotionSettings]);

  return (
    <div className="relative font-manrope flex flex-col h-full min-h-0 bg-white rounded-[5px] md:rounded-[15px] overflow-hidden">
      <div className="absolute top-5 right-5 flex items-center gap-2 z-[40] pointer-events-auto">
        {onExpandToggle && (
          <button
            type="button"
            onClick={onExpandToggle}
            className="pointer-events-auto flex items-center gap-1.5 text-[#2C3038] hover:text-[#585858] text-sm font-manrope px-2 py-1 rounded hover:bg-[#E5E5E5]"
            aria-label={isExpanded ? 'Свернуть панель' : 'Расширить панель'}
          >
            {isExpanded ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                </svg>
                Свернуть
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zm-3-12v2h3v3h2V5h-5z" />
                </svg>
                Расширить
              </>
            )}
          </button>
        )}
        <button type="button" onClick={onClose} className="pointer-events-auto cursor-pointer" aria-label="Закрыть">
          <CloseCircleCrossBtnHover width={30} height={30} className="" {...CLOSE_BTN_COLOR_PROPS} />
        </button>
      </div>
      <div
        className={classNames(
          'relative flex items-center gap-3 flex-shrink-0 p-4 pb-2',
          !canShowResults && 'border-b border-[#E5E5E5]',
        )}
      >
        <div className="flex items-baseline gap-3 min-w-0 flex-1 flex-wrap">
          <h2 className="font-manrope font-bold text-2xl text-[#2C3038] shrink-0">Детектор эмоций</h2>
          {canShowResults && (
            <p className="font-manrope text-[#585858] text-lg truncate min-w-0">
              Клиенты сделали выбор. Выберите участника, чтобы посмотреть результат.
            </p>
          )}
        </div>
        {!canShowResults && expectedParticipantIds.length > 0 && (
          <div className="flex items-center gap-3 shrink-0">
            <p className="font-manrope text-[#2C3038] text-lg font-medium tabular-nums">
              Ответили: {respondedIds.length} из {expectedParticipantIds.length}
            </p>
            {respondedIds.length > 0 && (
              <Button type="primary" className="!text-sm py-1.5 px-3" onClick={() => setForceShowResults(true)}>
                Показать результаты
              </Button>
            )}
          </div>
        )}
        <div className="flex-1 min-w-0" aria-hidden />
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {!canShowResults ? (
          <>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-4 white-scroll">
              <div className="flex flex-col gap-5 text-[#2C3038] text-lg font-manrope">
                {GROUP_EMOTIONS_INSTRUCTIONS.map((block, i) => (
                  <div key={i}>
                    <p className="font-bold mb-1.5 text-[1.125rem]">{block.title}</p>
                    <ul className="list-disc list-inside space-y-1 text-[#585858] text-lg">
                      {block.lines.map((line, j) => (
                        <li key={j}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0 px-4 pb-4 flex flex-col items-center gap-2">
              <span className="inline-flex items-center px-10 py-2 rounded-[10px] bg-[#C00000]/50 text-white text-lg font-manrope font-medium">
                Клиенты выбирают...
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 pb-3 mb-3 shrink-0 px-4">
              {respondedIds.map((id) => {
                const idx = expectedParticipantIds.indexOf(id);
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setActiveTabId(id);
                      const numLabel = String(participantNumber(idx >= 0 ? idx : 0));
                      const labelForClient = participantDisplayNames?.[id] ?? numLabel;
                      const emo = clientSelectionsByParticipant[id]?.emo ?? [];
                      onActiveTabChange?.(labelForClient, emo);
                    }}
                    title={participantDisplayNames?.[id]}
                    className={classNames(
                      'flex items-center justify-center w-9 h-9 rounded-full text-lg font-medium transition-colors',
                      effectiveActiveId === id
                        ? 'bg-[#C00000] text-white border-2 border-white'
                        : 'bg-[#E5E5E5] text-[#2C3038] hover:bg-[#D5D5D5]',
                    )}
                  >
                    {participantNumber(idx >= 0 ? idx : 0)}
                  </button>
                );
              })}
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 pb-4 white-scroll">
              {activeEmo.length > 0 ? (
                emotionsByGroup.length > 0 ? (
                  <div className="flex flex-col gap-4 w-full">
                    {emotionsByGroup.map((group) => {
                      const IconComponent = emotionIcons[group.category];
                      const count = group.items.length;

                      return (
                        <div
                          key={group.category}
                          className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 rounded-[10px] bg-white shadow-[0px_4px_12px_rgba(0,0,0,0.06)] border border-[#EAEBEA] min-w-0"
                        >
                          <div className="flex items-center gap-3 shrink-0">
                            <div className="w-[80px] h-[80px] min-w-[80px] min-h-[80px] bg-grey5 rounded-[10px] bg-grey5 flex items-center justify-center">
                              {IconComponent ? (
                                <IconComponent
                                  needHover={false}
                                  stroke="#63656A"
                                  className="w-[50px] h-[50px] !cursor-default"
                                />
                              ) : null}
                            </div>
                            <div className="flex flex-col">
                              <p className="font-manrope font-bold text-[#2C3038] text-lg">{group.category}</p>
                              <p className="font-manrope text-[#585858] text-sm">
                                {count} {declOfNum(count, ['эмоция', 'эмоции', 'эмоций'])}
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center min-w-0 flex-1">
                            {group.items.map((item, itemIdx) => (
                              <span
                                key={`${group.category}-${itemIdx}`}
                                className="bg-[#E5E5E5] text-[#2C3038] font-medium px-3 py-1.5 rounded-[5px] text-sm"
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex gap-[15px] flex-wrap w-full">
                    {activeEmo.map((el, idx) => (
                      <div
                        className="bg-[#E5E5E5] rounded-[5px] py-2 px-2 lg:py-[5px] lg:px-[10px] text-[16px] text-[#2C3038] font-semibold"
                        key={idx}
                      >
                        {el}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                <p className="font-manrope text-[#585858] text-lg">
                  {respondedIds.includes(effectiveActiveId ?? '') ? 'Нет выбранных эмоций' : 'Участник ещё не ответил'}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
